"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.heal = heal;
const options_1 = require("./options");
const NUMERIC_RANGE_TYPES = ['SLIDER', 'UPSCALE_FACTOR_SLIDER', 'CFG', 'STEPS'];
function heal(value, field) {
    if (NUMERIC_RANGE_TYPES.indexOf(field.type) !== -1) {
        return healNumericRange(value, field);
    }
    if (field.type === 'SINGLE_SELECT') {
        return healSingleSelect(value, field);
    }
    if (field.type === 'WIDTH_HEIGHT') {
        return healWidthHeight(value, field);
    }
    return value;
}
// Mirrors web's normalisation: unwrap a `{value}` envelope, then match against
// the declared options.
//
// Exact match runs FIRST, so an option declared with a `null` value (a "None"
// choice meaning "send nothing to the provider") is a real, selectable option
// rather than something that always heals away to the default.
//
// The `String()` pass that follows exists so a stringified payload ("2") still
// matches numeric option 2. It is restricted to string/number on BOTH sides:
// unrestricted, `String(null)` is `"null"`, so a dropdown that stringified its
// value would have the literal text "null" accepted here as if the user had
// chosen None, and that text then reached the provider.
function matchesByValue(value, options) {
    let compare = value;
    if (value && typeof value === 'object' && 'value' in value) {
        compare = value.value;
    }
    if (options.some((opt) => opt.value === compare))
        return true;
    if (typeof compare !== 'string' && typeof compare !== 'number')
        return false;
    return options.some((opt) => (typeof opt.value === 'string' || typeof opt.value === 'number') &&
        String(opt.value) === String(compare));
}
function healSingleSelect(value, field) {
    const options = (0, options_1.getSingleSelectOptions)(field.display);
    if (options.length === 0)
        return field.defaultValue;
    return matchesByValue(value, options) ? value : field.defaultValue;
}
function healWidthHeight(value, field) {
    const display = field.display;
    const select = display && display.select;
    if (!select || !Array.isArray(select.options) || select.options.length === 0) {
        return value;
    }
    const options = select.options;
    if (select.format === 'wh') {
        if (value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            typeof value.width === 'number' &&
            typeof value.height === 'number') {
            const w = value.width;
            const h = value.height;
            const match = options.some((opt) => opt.width === w && opt.height === h);
            return match ? value : field.defaultValue;
        }
        return field.defaultValue;
    }
    return matchesByValue(value, options) ? value : field.defaultValue;
}
function healNumericRange(value, field) {
    if (value === null || value === undefined || value === '')
        return field.defaultValue;
    const display = (field.display || {});
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n))
        return field.defaultValue;
    let clamped = n;
    if (typeof display.min === 'number' && clamped < display.min)
        clamped = display.min;
    if (typeof display.max === 'number' && clamped > display.max)
        clamped = display.max;
    // Step alignment only applies to SLIDER variants; CFG/STEPS in the web client
    // treat step alignment as optional. Mirror the web's "requireStepAlignment"
    // gate: opt in for SLIDER + UPSCALE_FACTOR_SLIDER when a step is declared.
    const requiresStepAlign = (field.type === 'SLIDER' || field.type === 'UPSCALE_FACTOR_SLIDER') &&
        typeof display.step === 'number' &&
        typeof display.min === 'number' &&
        display.step > 0;
    if (!requiresStepAlign)
        return clamped;
    const min = display.min;
    const step = display.step;
    const stepsFromMin = (clamped - min) / step;
    const rounded = Math.round(stepsFromMin);
    const expected = min + rounded * step;
    const tolerance = step * 0.0001;
    if (Math.abs(clamped - expected) <= tolerance)
        return clamped;
    return expected;
}
